import React from "react";
import Head from "next/head";
import {
  PressRelease,
  ContentfulContentType,
} from "@tiicker/util/lib/contentful/types";
import LoadingBlock from "components/Loading/LoadingBlock";
import * as DateIso from "@tiicker/util/lib/date-iso";
import ArticleMainContent from "components/Articles/ArticleMainContent/ArticleMainContent";
import ArticleRichText from "components/Articles/ArticleRichText/ArticleRichText";
import { AdCampaign } from "components/AdCampaign/AdCampaign";
import styles from "./news.module.scss";
import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import { GetStaticPaths, GetStaticProps } from "next";
import getBaseUrl from "@tiicker/util/lib/get-base-url";
import { Asset, EntryCollection } from "contentful";
import { useRouter } from "next/router";
import SimpleLayout from "components/SimpleLayout/SimpleLayout";
import { parseIncludesForItem } from "@tiicker/util/lib/contentful/helpers";
import Four04 from "components/404/404";

interface Props {
  slug: string;
  pressReleases: EntryCollection<PressRelease> | null;
}

const PressReleaseDisplay = (props: Props) => {
  const router = useRouter();
  const { isFallback } = router;

  const currentURL = `${getBaseUrl()}news/${props.slug}`;

  if (isFallback) {
    const pageDesc = `News and information about TiiCKER. Reinventing how individual investors are rewarded for brand loyalty and creates a new, high-affinity audience to the product marketing mix: shareholders. ${props.slug}`;
    return (
      <>
        <Head>
          <title>News Article | TiiCKER</title>
          <meta property="og:url" key="url" content={currentURL} />
          <meta name="description" key="meta-description" content={pageDesc} />
          <meta
            property="og:description"
            key="description"
            content={pageDesc}
          />
        </Head>
        <LoadingBlock />
      </>
    );
  }

  if (props.pressReleases === null) {
    return <Four04 />;
  }

  const content = props.pressReleases.items[0];

  const asset = content.fields.heroImage
    ? (parseIncludesForItem(
        props.pressReleases.includes,
        content.fields.heroImage.sys.id
      ) as unknown as Asset)
    : undefined;

  return (
    <SimpleLayout
      title={content.fields.title}
      description={content.fields.description}
      url={currentURL}
      imageUrl={asset ? asset.fields.file.url : undefined}
      date={DateIso.toIsoDate(content.fields.publishedDate)}
      symbols={[]}
      slug={content.fields.slug}
    >
      <ArticleMainContent
        title={content.fields.title}
        slug={content.fields.slug}
        sidebarContent={
          <>
            <div>
              <AdCampaign
                size="md-rectangle"
                className={styles.sidebarAd}
              ></AdCampaign>
            </div>

            {/* <PressReleaseSidebar currentPressReleaseId={content.sys.id} /> */}

            <div>
              <AdCampaign size="half-page"></AdCampaign>
            </div>
          </>
        }
      >
        <ArticleRichText
          richText={content.fields.content}
          contentIncludes={props.pressReleases.includes}
        />
      </ArticleMainContent>
    </SimpleLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const client = getContentfulClient("contentful");
  const allPressReleases = await client.getEntries<PressRelease>({
    content_type: ContentfulContentType.PressRelease,
    select: "fields.slug",
  });

  const paths = allPressReleases.items.map(
    (article) => `/news/${article.fields.slug}`
  );
  return { paths: paths, fallback: true };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params && params.slug ? params.slug : "";

  if (Array.isArray(slug) || slug === "") {
    return {
      props: {
        slug: null,
      },
      revalidate: 100,
    };
  }

  const client = getContentfulClient("contentful");
  const pressReleases = await client.getEntries<PressRelease>({
    content_type: ContentfulContentType.PressRelease,
    limit: 1,
    "fields.slug[in]": slug,
  });

  if (pressReleases.items.length === 0) {
    return {
      props: {
        slug,
        pressReleases: null,
        openGraphTwitterData: [],
      },
      revalidate: 100,
    };
  }

  const content = pressReleases.items[0];
  const asset = content.fields.heroImage
    ? (parseIncludesForItem(
        pressReleases.includes,
        content.fields.heroImage.sys.id
      ) as unknown as Asset)
    : undefined;

  const openGraphTwitterData = [
    {
      property: "og:image",

      content: asset
        ? "https:" + asset.fields.file.url
        : `${getBaseUrl()}images/TiickerTM-WhiteBackground.png`,
      key: "ogimage",
    },
    {
      property: "og:image:width",
      content: "800",
      key: "ogimagewidth",
    },
    {
      property: "og:image:height",
      content: "600",
      key: "ogimageheight",
    },
    {
      property: "og:url",
      content: `${getBaseUrl()}news/${slug}`,
      key: "ogurl",
    },
    {
      property: "og:title",
      content: content.fields.title,
      key: "ogtitle",
    },
    {
      property: "og:description",
      content: content.fields.description,
      key: "ogdesc",
    },
    {
      property: "og:type",
      content: "article",
      key: "website",
    },
    {
      property: "twitter:image",
      content: asset
        ? "https:" + asset.fields.file.url
        : `${getBaseUrl()}images/TiickerTM-WhiteBackground.png`,
    },
  ];

  const title = content.fields.title;
  const description = content.fields.description;

  return {
    props: {
      slug,
      pressReleases,
      openGraphTwitterData,
      title,
      description,
    },
    revalidate: 100,
  };
};

export default PressReleaseDisplay;
